import { writable } from 'svelte/store';
import collect from 'collect.js';
// Models
import Market from '@/Models/Market.js';
// Constants
import config from '@/config.js';
import { Status } from '@/Constants/Market.js';
import { EventStatus } from '@/Constants/Event.js';
// Stores
import {
	mergeCollectionStore,
	replaceModelInCollectionStore,
	mergeModelDataInCollectionStore,
	replaceOrAddModelInCollectionStore
} from '@/Stores/StoreHelpers.js';
import SportSelectionStore from '@/Stores/Sport/SportSelectionStore.js';
import { notify } from '@/Services/Notify.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	let _store;
	let meta;

	const { subscribe, set, update } = writable(collect());

	subscribe((current) => (_store = current));

	const methods = {
		getPagination() {
			return meta && meta.pagination;
		},

		getMarketWithSelections(marketId, selectionsOnly = false) {
			if (!config.SPORT_ENABLED) return;

			const market = new Market();
			return market
				.with(['selections'])
				.find(marketId)
				.then((result) => {
					let allSportSelections = collect();

					result.selections.data.map((selection) => {
						selection.market_id = result.id;

						allSportSelections.push(selection);
					});
					delete result.selections;
					mergeCollectionStore(SportSelectionStore, allSportSelections);

					if (!selectionsOnly) replaceOrAddModelInCollectionStore(result, this);
				});
		},

		getUnresultedMarkets(pagination = null) {
			if (!config.SPORT_ENABLED) return;

			let market = new Market();
			market = market
				.where('min_bet_count', 1)
				.where('event_status_id', [EventStatus.Closed, EventStatus.MatchOver].join(','))
				.where('not_status_id', Status.Resulted)
				.with('sport_event')
				.orderByDesc('event_start_time');
			let results = pagination ? market.paginate(pagination.size, pagination.number) : market.get();

			return results.then((result) => {
				meta = result.meta;
				set(result.data);
			});
		},

		updateManualResults(marketId, manualResults) {
			if (!config.SPORT_ENABLED) return;

			try {
				return _store
					.firstWhere('id', marketId)
					.manualResult(manualResults)
					.then((results) => {
						return results;
					});
			} catch (error) {
				return new Promise(() => {
					throw new Error('Unable to update results for market');
				});
			}
		},

		updateMarketOverrideStatus(marketId, override_status_id) {
			if (!config.SPORT_ENABLED) return;

			let market = _store.firstWhere('id', marketId);
			return market
				.patch({
					override_status_id
				})
				.then((results) => {
					replaceModelInCollectionStore(results, this);
					return results;
				});
		},

		updateMarketOverridePrices(marketId, selectionPrices = []) {
			if (!config.SPORT_ENABLED) return;

			let market = _store.firstWhere('id', marketId);
			return market
				.overridePrices(selectionPrices)
				.then((results) => {
					this.getMarketWithSelections(marketId);
					return results;
				})
				.catch((e) => {
					notify({
						type: 'danger',
						title: 'Error!',
						message: (e.error && e.error[0]) || 'An error occurred',
						timeout: 5
					});
				});
		},

		removeMarketOverridePrices(marketId) {
			if (!config.SPORT_ENABLED) return;

			let market = _store.firstWhere('id', marketId);
			return market.removeOverridePrices().then((results) => {
				this.getMarketWithSelections(marketId);
				return results;
			});
		},

		updateManualResult(marketId, manual_result) {
			if (!config.SPORT_ENABLED) return;

			return _store
				.firstWhere('id', marketId)
				.patch({ manual_result })
				.then((results) => {
					// keep our local collection in sync
					mergeModelDataInCollectionStore(results, this);
					return results;
				});
		},

		updateMarketBoostable(marketId, boostable) {
			if (!config.SPORT_ENABLED) return;

			return _store
				.firstWhere('id', marketId)
				.patch({ boostable })
				.then((results) => {
					// keep our local collection in sync
					mergeModelDataInCollectionStore(results, this);
					return results;
				});
		},

		updateMarketBonusDisabled(marketId, bonus_disabled) {
			if (!config.SPORT_ENABLED) return;

			return _store
				.firstWhere('id', marketId)
				.patch({ bonus_disabled })
				.then((results) => {
					// keep our local collection in sync
					mergeModelDataInCollectionStore(results, this);
					return results;
				});
		},

		updateMarketMultis(marketId, multis) {
			if (!config.SPORT_ENABLED) return;

			return _store
				.firstWhere('id', marketId)
				.patch({ multis })
				.then((results) => {
					// keep our local collection in sync
					mergeModelDataInCollectionStore(results, this);
					return results;
				});
		},

		updateMarketCashout(marketId, cashout) {
			if (!config.SPORT_ENABLED) return;

			return _store
				.firstWhere('id', marketId)
				.patch({ cashout_enabled: cashout })
				.then((results) => {
					// keep our local collection in sync
					mergeModelDataInCollectionStore(results, this);
					return results;
				});
		},

		updateMarketInPlayTrading(marketId, tradingInPlay) {
			if (!config.SPORT_ENABLED) return;

			return _store
				.firstWhere('id', marketId)
				.patch({ traded_in_play: tradingInPlay })
				.then((results) => {
					// keep our local collection in sync
					mergeModelDataInCollectionStore(results, this);
					return results;
				});
		},

		updateMarketPreMatchTrading(marketId, tradingPreMatch) {
			if (!config.SPORT_ENABLED) return;

			return _store
				.firstWhere('id', marketId)
				.patch({ traded_pre_match: tradingPreMatch })
				.then((results) => {
					// keep our local collection in sync
					mergeModelDataInCollectionStore(results, this);
					return results;
				});
		},

		applyMarketUpdates(data) {
			if (data && data.id && _store.firstWhere('id', String(data.id))) {
				mergeModelDataInCollectionStore(data, this);
			}
		},

		createManualMarket(data) {
			let market = new Market();

			return market
				.makePersistRequest({
					url: market.resourceUrl(),
					method: 'PUT',
					data
				})
				.then((result) => {
					return result;
				});
		},

		createDerivedMarkets(market, derivedMarkets) {
			return market
				.makePersistRequest({
					url: `${market.resourceUrl()}${market.id}/derived_markets`,
					method: 'post',
					data: derivedMarkets
				})
				.then((result) => {
					return result;
				});
		}
	};

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
